import { useCallback, useEffect, useRef, useState } from "react";
import { apiClient } from "../App";
import { toast } from "sonner";

const LABELS = { zoom: "Zoom", microsoft: "Outlook" };

/**
 * Connect Zoom / Outlook without leaving the page.
 *
 * The consent screen opens in a small window, lands on /oauth-complete, and that
 * page postMessages back before closing itself. That matters inside the booking
 * dialog: a PM who hasn't connected yet can connect mid-draft and keep going
 * instead of losing a half-filled meeting to a full-page redirect.
 *
 * If the popup is blocked we fall back to a normal redirect, which is the same
 * flow the Settings cards use.
 *
 * @param onConnected called with the provider id after a successful connection
 */
export default function useMeetingConnect(onConnected) {
  const [connecting, setConnecting] = useState(null);
  const popupRef = useRef(null);
  const pollRef = useRef(null);
  const onConnectedRef = useRef(onConnected);

  useEffect(() => {
    onConnectedRef.current = onConnected;
  }, [onConnected]);

  const stopWatching = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    popupRef.current = null;
    setConnecting(null);
  }, []);

  useEffect(() => {
    const onMessage = (event) => {
      if (event.origin !== window.location.origin) return;
      const data = event.data;
      if (!data || data.type !== "hq-meeting-oauth") return;
      const label = LABELS[data.provider] || "Account";
      if (data.ok) {
        toast.success(`${label} connected`);
        onConnectedRef.current?.(data.provider);
      } else {
        toast.error(`Could not connect ${label}`);
      }
      stopWatching();
    };
    window.addEventListener("message", onMessage);
    return () => {
      window.removeEventListener("message", onMessage);
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [stopWatching]);

  const connect = useCallback(
    async (provider) => {
      setConnecting(provider);
      try {
        const res = await apiClient.post(
          `/meetings/integrations/${provider}/authorize?return_to=${encodeURIComponent(
            "/oauth-complete"
          )}`
        );
        const url = res.data?.authorize_url;
        if (!url) throw new Error("No authorize URL");

        const popup = window.open(
          url,
          "hq-meeting-oauth",
          "width=620,height=780,menubar=no,toolbar=no"
        );
        if (!popup) {
          // Popup blocked — send the whole tab and come back to the hub.
          const fallback = await apiClient.post(
            `/meetings/integrations/${provider}/authorize?return_to=${encodeURIComponent("/meetings")}`
          );
          window.location.href = fallback.data?.authorize_url || url;
          return;
        }
        popupRef.current = popup;
        popup.focus?.();

        // The popup may be closed by hand without ever reporting back; re-check
        // the connection state when that happens so the UI doesn't sit spinning.
        pollRef.current = setInterval(() => {
          if (popupRef.current?.closed) {
            stopWatching();
            onConnectedRef.current?.(provider);
          }
        }, 700);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Could not start the connection");
        setConnecting(null);
      }
    },
    [stopWatching]
  );

  return { connect, connecting };
}
